import { useState, useCallback } from 'react';
import { BookOpen, Code2 } from 'lucide-react';
import { createPortal } from 'react-dom';
import { Skeleton } from "@/components/ui/skeleton";

interface FloatingDocsModalProps {
    path?: string;
    icon?: 'docs' | 'api';
    label?: string;
}

const FloatingDocsModal = ({ path = '', icon = 'docs', label = 'Documentation' }: FloatingDocsModalProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const Icon = icon === 'docs' ? BookOpen : Code2;

    const openModal = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(true);
        setIsLoading(true);
    }, []);

    const handleIframeLoad = useCallback(() => {
        setIsLoading(false);
    }, []);

    const handleBackdropClick = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.target === e.currentTarget) {
            setIsOpen(false);
        }
    }, []);

    const stopPropagation = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    return (
        <>
            <button
                onClick={openModal}
                onMouseDown={stopPropagation}
                onMouseUp={stopPropagation}
                className="w-full flex items-center px-2 py-1.5 relative cursor-pointer select-none  rounded-sm text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 hover:bg-accent hover:text-accent-foreground"
            >
                <Icon className="mr-2 h-4 w-4" />
                <span>{label}</span>
            </button>

            {isOpen && createPortal(
                <div
                    className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 z-[100]"
                    onClick={handleBackdropClick}
                    onMouseDown={stopPropagation}
                    onMouseUp={stopPropagation}
                >
                    <div
                        className="relative w-[90vw] h-[90vh] bg-white rounded-lg shadow-lg overflow-hidden"
                        onClick={stopPropagation}
                        onMouseDown={stopPropagation}
                        onMouseUp={stopPropagation}
                    >
                        <div
                            className="w-full h-full relative"
                            onClick={stopPropagation}
                            onMouseDown={stopPropagation}
                            onMouseUp={stopPropagation}
                        >
                            {isLoading && (
                                <div className="absolute inset-0 bg-background">
                                    {/* Header */}
                                    <div className="border-b">
                                        {/* Top row with search and theme */}
                                        <div className="h-14 flex items-center justify-between px-16">
                                            <div className="w-40"></div> {/* Spacer to help center search */}
                                            
                                            {/* Center search bar */}
                                            <div className="flex justify-center">
                                                <div className="w-72 flex items-center gap-2 border rounded-lg px-3 py-1.5">
                                                    <Skeleton className="h-4 w-4" /> {/* Search icon */}
                                                    <Skeleton className="h-4 w-32" /> {/* Search text */}
                                                    <div className="ml-auto flex items-center gap-1 opacity-60">
                                                        <Skeleton className="h-3 w-8" /> {/* Ctrl */}
                                                        <Skeleton className="h-3 w-4" /> {/* K */}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Right theme toggle */}
                                            <div className="w-40 flex justify-end">
                                                <Skeleton className="h-8 w-8 rounded-full" /> {/* Theme toggle button */}
                                            </div>
                                        </div>

                                        {/* Bottom row with tabs */}
                                        <div className="h-14 flex items-center px-16">
                                            <div className="flex gap-8">
                                                <Skeleton className="h-4 w-28 bg-blue-100" />
                                                <Skeleton className="h-4 w-24" />
                                                <Skeleton className="h-4 w-28" />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex h-[calc(100%-3.5rem)] px-16">
                                        {/* Left Sidebar */}
                                        <div className="w-64 border-r p-6 space-y-8">
                                            {/* Get Started Section */}
                                            <div className="space-y-4">
                                                <Skeleton className="h-5 w-24" /> {/* Get Started */}
                                                <div className="space-y-3 pl-1">
                                                    <Skeleton className="h-4 w-20" /> {/* Introduction */}
                                                </div>
                                            </div>

                                            {/* Guides Section */}
                                            <div className="space-y-4">
                                                <Skeleton className="h-5 w-16" /> {/* Guides */}
                                                <div className="space-y-3 pl-1">
                                                    <Skeleton className="h-4 w-40" /> {/* Architecture Overview */}
                                                    <Skeleton className="h-4 w-16" /> {/* Pricing */}
                                                    <Skeleton className="h-4 w-24" /> {/* Enterprise */}
                                                    <Skeleton className="h-4 w-28" /> {/* System Status */}
                                                </div>
                                            </div>

                                            {/* Quick Start Section */}
                                            <div className="space-y-4">
                                                <Skeleton className="h-5 w-24" /> {/* Quick Start */}
                                                <div className="space-y-3 pl-1">
                                                    <Skeleton className="h-4 w-36" /> {/* Dashboard Overview */}
                                                    <Skeleton className="h-4 w-28" /> {/* Authentication */}
                                                    <Skeleton className="h-4 w-20" /> {/* Analytics */}
                                                    <Skeleton className="h-4 w-24" /> {/* Workflows */}
                                                    <Skeleton className="h-4 w-24" /> {/* Assistants */}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Main Content */}
                                        <div className="flex-1 p-8 space-y-6 max-w-4xl">
                                            <div>
                                                <Skeleton className="h-12 w-48 mb-4" /> {/* Introduction */}
                                                <Skeleton className="h-5 w-[500px]" /> {/* Build Powerful Voice AI... */}
                                            </div>

                                            <div className="py-12 space-y-4">
                                                <div className="space-y-3">
                                                    <Skeleton className="h-12 w-[450px]" /> {/* Voice AI Made Simple */}
                                                    <Skeleton className="h-5 w-[600px]" /> {/* Design natural conversations... */}
                                                </div>
                                            </div>

                                            <div className="space-y-4">
                                                <Skeleton className="h-8 w-[400px]" /> {/* Transform Voice Communication */}
                                                <div className="space-y-2">
                                                    <Skeleton className="h-5 w-full" />
                                                    <Skeleton className="h-5 w-5/6" />
                                                    <Skeleton className="h-5 w-2/3" />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Right sidebar */}
                                        <div className="w-64 border-l p-6 space-y-4">
                                            <div className="flex items-center gap-2">
                                                <Skeleton className="h-4 w-4" /> {/* Icon */}
                                                <Skeleton className="h-4 w-24" /> {/* On this page */}
                                            </div>
                                            <div className="space-y-3 pl-1">
                                                <Skeleton className="h-4 w-48" /> {/* Transform Voice Communication */}
                                                <Skeleton className="h-4 w-40" /> {/* Seamless Integrations */}
                                                <div className="pl-4 space-y-2">
                                                    <Skeleton className="h-3 w-32" /> {/* Storage & Data */}
                                                    <Skeleton className="h-3 w-44" /> {/* Communication & Collaboration */}
                                                    <Skeleton className="h-3 w-28" /> {/* Business & CRM */}
                                                </div>
                                                <Skeleton className="h-4 w-24" /> {/* Quick Stats */}
                                                <Skeleton className="h-4 w-28" /> {/* Key Features */}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <iframe
                                src={`https://docs-proxy.voicebot.studio${path}`}
                                title="Voicebot Docs"
                                className="w-full h-full rounded-lg"
                                style={{
                                    border: 'none',
                                    pointerEvents: 'auto',
                                    opacity: isLoading ? 0 : 1,
                                    transition: 'opacity 0.2s ease-in-out'
                                }}
                                onLoad={handleIframeLoad}
                            />
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default FloatingDocsModal;
